import { v4 as uuidv4 } from 'uuid';
import { captureException } from '@sentry/nextjs';

import { MessagingAvailabilityReason, MessagingStatus } from 'providers/messaging/types';
import { isValidMessagingAvailabilityReason } from 'providers/messaging/type-guards';
import { sendCounterMetric } from 'utils/metrics';
import { Vertical } from 'utils/vertical';
import { deleteCookie } from 'utils/cookies';
import { getLocalStorageContext } from 'utils/localStorage/localStorageHelper';

import setupLpTag from './liveEngageTag.generated';
import { getCurrentConfig, UNLAUNCHED_LOCALES } from './config';
import { getSDEs, sendChatData } from './utils';
import { LpChatMessage } from './types';
import { chatMessageListener } from './chatEvents';

const STATE_TIMEOUT = 20000;
const STORAGE_KEY = 'LP_CHAT';
const KEY_INITIATED_BY = 'INITIATED_BY';

const lpChatLocalStorage = getLocalStorageContext(STORAGE_KEY);

const { siteId, locales } = getCurrentConfig();
let currentEngagementId: number;

const isAllowUnlaunchedFlagPresent = () => {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  return urlParams.get('lp_messaging_allow_unlaunched') === 'true';
};

const getEngagementId = (locale: string): number => {
  const engagementId = locales[locale];
  if (!engagementId) {
    return 0;
  }
  const isLaunched = !UNLAUNCHED_LOCALES.includes(locale);
  return isLaunched || isAllowUnlaunchedFlagPresent() ? engagementId : 0;
};

const getLpDivId = () => `LP_DIV_${currentEngagementId}`;

const createLpContainer = () => {
  const id = getLpDivId();
  if (!window.document.getElementById(`${id}`)) {
    const el = window.document.createElement('div');
    el.setAttribute('id', id);
    window.document.body.append(el);
  }
};

const updateEngagement = (locale: string) => {
  createLpContainer();
  window.lpTag?.newPage?.(window.location.href, {
    section: [locale],
    sdes: [],
    taglets: {
      rendererStub: {
        divIdsToKeep: {
          [getLpDivId()]: true,
        },
      },
    },
  });
};

export const clearPreviousConversation = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // delete lp identifier cookie
    ['LPVID', `LPSID-${siteId}`].forEach(cookie => {
      deleteCookie(cookie, '/', '.spotify.com');
    });

    // delete lp indexDB database if indexDB exists
    if (typeof indexedDB === 'undefined') {
      resolve();
      return;
    }

    const request = indexedDB.deleteDatabase('LPSecureStorage');

    request.onsuccess = () => {
      sendCounterMetric('live-engage-clear-conversation', { outcome: 'success' });
      resolve();
    };

    request.onerror = () => {
      sendCounterMetric('live-engage-clear-conversation', { outcome: 'error' });
      reject(new Error(MessagingAvailabilityReason.LIVE_ENGAGE_CLEANING_ERROR));
    };
  });
};

const onMessagingReady = ({ callback }: { callback: Function }) => {
  // when engagement is ready
  window.lpTag.events.bind({
    eventName: 'AFTER_CREATE_ENGAGEMENT_INSTANCE',
    appName: 'RENDERER_STUB',
    func: callback,
    triggerOnce: true,
  });

  let hideWelcomeMessage = false;
  window.lpTag.hooks = window.lpTag.hooks || [];

  window.lpTag.hooks.push({
    name: 'AFTER_GET_LINES',
    callback: (options: { data: { lines?: LpChatMessage[] } }) => {
      const { lines } = options.data;
      if (lines) {
        chatMessageListener(lines);

        const [firstLine] = lines;

        try {
          if (
            firstLine &&
            firstLine.text &&
            typeof firstLine.text !== 'string' &&
            Array.isArray(firstLine.text.elements) &&
            firstLine.text.elements.find(el => el.tag === 'restartConversation')
          ) {
            hideWelcomeMessage = true;
            return options;
          }
        } catch (e) {
          captureException(e);
        }

        try {
          if (firstLine && firstLine.isWelcomeMessage) {
            if (hideWelcomeMessage) {
              hideWelcomeMessage = false;
              options.data.lines = [];
              return options;
            }
            return options;
          }
          return options;
        } catch (e) {
          captureException(e);
        }
      }

      return options;
    },
  });
};

const setupMessaging = () =>
  new Promise<void>(callback => {
    onMessagingReady({
      callback,
    });
  });

export const startLiveEngage = async (market: string, locale: string, vertical: Vertical) => {
  const supportRequestId = uuidv4();
  const spoSDEs = await getSDEs(supportRequestId);

  sendChatData(market, supportRequestId);
  window.lpTag.sdes = window.lpTag.sdes || [];
  window.lpTag.sdes.push(spoSDEs);
  window.lpTag.taglets.rendererStub.click(currentEngagementId);

  sendCounterMetric('live-engage-conversation-started', { locale, vertical });
};

const init = async (): Promise<MessagingStatus> => {
  return new Promise(async resolve => {
    const stateTimeout = setTimeout(() => {
      resolve({
        isAvailable: false,
        reason: MessagingAvailabilityReason.TIMEOUT,
      });
    }, STATE_TIMEOUT);

    await setupMessaging();
    clearTimeout(stateTimeout);

    resolve({
      isAvailable: true,
      reason: MessagingAvailabilityReason.AVAILABLE,
    });
  });
};

export const setup = async (locale: string, username?: string): Promise<MessagingStatus> => {
  currentEngagementId = getEngagementId(locale);
  if (!currentEngagementId) {
    return {
      isAvailable: false,
      reason: MessagingAvailabilityReason.UNSUPPORTED_LOCALE,
    };
  }

  try {
    const initiatedBy = lpChatLocalStorage.getValue(KEY_INITIATED_BY);

    if (username !== initiatedBy) {
      if (initiatedBy !== null) {
        await clearPreviousConversation();
      }

      if (username) {
        lpChatLocalStorage.setValue(KEY_INITIATED_BY, username);
      } else {
        lpChatLocalStorage.removeValue(KEY_INITIATED_BY);
      }
    }

    setupLpTag(siteId);
    updateEngagement(locale);

    return init();
  } catch (error) {
    captureException(error);

    return {
      isAvailable: false,
      reason: isValidMessagingAvailabilityReason(error.message)
        ? error.message
        : MessagingAvailabilityReason.LIVE_ENGAGE_ERROR,
    };
  }
};
